import { cx } from "@tripcart/traveler-ui/cx";

import styles from "./_price-figure.module.css";

/**
 * The guide's price figure — `.bk-amt` / `.lg-fig` / `.pk-fig`, each paired
 * with a `-cur` affix one type step down and a shade quieter.
 *
 * Why it exists: the number is what someone reads, and at 32px a currency
 * symbol rendered at the same size takes a third of the figure's width for
 * information they already have. The guide sets `$` to 22px beside a 32px
 * amount, and 13px beside 21px.
 *
 * Why it splits on a regex instead of asking the formatter: `formatMoney`
 * applies the AGENCY's own display template, which can put the symbol, the ISO
 * code, or nothing at all on either side ("$1,595", "1.595,00 €", "USD 1,595").
 * Rather than re-implement that template here — a second place to keep in sync
 * — this takes the formatted string and peels off whatever non-digit run sits
 * at each end. That is affix-position agnostic by construction, so a suffix
 * currency shrinks exactly like a prefix one.
 *
 * A minus sign would land in the prefix and shrink with it. Prices on this page
 * are never negative, and a shrunk minus is a cosmetic problem rather than a
 * wrong number, so it is not worth a special case.
 */
export function PriceFigure({
  formatted,
  className,
  affixClassName,
}: {
  /** The output of `formatMoney` / `useFormatMoney` — already localised. */
  formatted: string;
  /** Type for the digits. */
  className?: string;
  /** Type for the currency affix — one step down from `className`. */
  affixClassName?: string;
}) {
  const { prefix, digits, suffix } = splitCurrencyAffixes(formatted);
  return (
    <span className={cx(styles.figure, className)}>
      {prefix ? <span className={affixClassName}>{prefix}</span> : null}
      {digits}
      {suffix ? <span className={affixClassName}>{suffix}</span> : null}
    </span>
  );
}

/** Peel the non-digit runs off each end of a formatted money string. A string
 *  with no digits at all (never produced by `formatMoney`, but cheap to be
 *  honest about) comes back whole in `digits`, so nothing is ever dropped. */
export function splitCurrencyAffixes(formatted: string): {
  prefix: string;
  digits: string;
  suffix: string;
} {
  const first = formatted.search(/\d/);
  if (first === -1) return { prefix: "", digits: formatted, suffix: "" };
  let last = formatted.length - 1;
  while (last >= 0 && !/\d/.test(formatted[last]!)) last -= 1;
  return {
    prefix: formatted.slice(0, first),
    digits: formatted.slice(first, last + 1),
    suffix: formatted.slice(last + 1),
  };
}
